import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { partnerSelector } from "@dashboard/config/redux";
import {
  fetchCancelLogistic,
  fetchDeliveryDetails,
  fetchLogisticProviders,
} from "../fetchers/logistic.fetchers";
import { deliveryActions } from "../store/delivery.slice";
import { useDeliveryToken } from "./use-delivery-token.hook";
import {
  DeliveryConfigurationData,
  DeliveryDetails,
  LogisticProviderConfig,
} from "../entities/delivery.entities";
import { fetchDeliveryConfigurationData } from "../services/remote-config.service";
import {
  findLogisticProviderForDetail,
  mapDeliveryDetailToOrderCalculation,
  mapDetailToProviderQuote,
  needsLogisticPayment,
  resolveDetailLinkedOrderId,
  resolveDetailSizeLabel,
  resolveDetailTypeLabel,
} from "../utils/delivery-detail.utils";
import {
  navigateToLogisticPayment,
  prepareLogisticCheckoutFromDetail,
} from "../utils/logistic-payment.utils";

export const useDeliveryDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const [detail, setDetail] = useState<DeliveryDetails | null>(null);
  const [deliveryConfig, setDeliveryConfig] =
    useState<DeliveryConfigurationData | null>(null);
  const [logisticProviders, setLogisticProviders] = useState<
    LogisticProviderConfig[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const userId = partner?.userId;
  const numericOrderId = orderId ? Number(orderId) : null;

  const loadDetail = useCallback(async () => {
    if (!token || !numericOrderId) return;
    setLoading(true);
    try {
      const data = await fetchDeliveryDetails(token, numericOrderId);
      setDetail(data);
      dispatch(deliveryActions.setCurrentDetail(data));
    } catch {
      toast.error(t("delivery.home.loadError"));
    } finally {
      setLoading(false);
    }
  }, [token, numericOrderId, dispatch, t]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    fetchDeliveryConfigurationData()
      .then(setDeliveryConfig)
      .catch(() => setDeliveryConfig({ types: [], sizes: [] }));
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchLogisticProviders(token)
      .then(setLogisticProviders)
      .catch(() => setLogisticProviders([]));
  }, [token]);

  const matchedProvider = useMemo(
    () =>
      detail ? findLogisticProviderForDetail(detail, logisticProviders) : null,
    [detail, logisticProviders]
  );

  const calculation = useMemo(() => {
    if (!detail || !numericOrderId) return null;
    return mapDeliveryDetailToOrderCalculation(detail, numericOrderId);
  }, [detail, numericOrderId]);

  const selectedProvider = useMemo(
    () =>
      detail ? mapDetailToProviderQuote(detail, matchedProvider) : null,
    [detail, matchedProvider]
  );

  const sizeLabel = useMemo(
    () =>
      detail ? resolveDetailSizeLabel(detail, deliveryConfig?.sizes) : "",
    [detail, deliveryConfig?.sizes]
  );

  const typeLabel = useMemo(
    () =>
      detail
        ? resolveDetailTypeLabel(
            detail,
            matchedProvider,
            deliveryConfig?.types
          )
        : "",
    [detail, matchedProvider, deliveryConfig?.types]
  );

  const linkedOrderId = useMemo(
    () => (detail ? resolveDetailLinkedOrderId(detail) : null),
    [detail]
  );

  const handlePay = useCallback(() => {
    if (!numericOrderId || !userId || !detail) return;
    navigateToLogisticPayment(
      dispatch,
      navigate,
      userId,
      prepareLogisticCheckoutFromDetail(detail, numericOrderId)
    );
  }, [dispatch, navigate, userId, numericOrderId, detail]);

  const handleCancel = useCallback(async () => {
    if (!token || !numericOrderId || cancelLoading) return;
    setCancelLoading(true);
    try {
      await fetchCancelLogistic(token, numericOrderId);
      toast.success(t("delivery.detail.cancel"));
      setShowCancelModal(false);
      await loadDetail();
    } catch {
      toast.error(t("delivery.settings.saveError"));
    } finally {
      setCancelLoading(false);
    }
  }, [token, numericOrderId, cancelLoading, loadDetail, t]);

  const openTracking = useCallback(() => {
    const url = detail?.raw?.trackingUrl;
    if (url) window.open(url, "_blank");
  }, [detail]);

  const openGuide = useCallback(() => {
    const url = detail?.guideUrl || detail?.raw?.manifestUrl;
    if (url) window.open(url, "_blank");
  }, [detail]);

  const isPendingPayment = detail ? needsLogisticPayment(detail) : false;

  const breadcrumbs = [
    {
      label: t("delivery.breadcrumb.delivery"),
      link: `/${userId}/delivery`,
    },
    { label: t("delivery.breadcrumb.detail") },
  ];

  return {
    detail,
    calculation,
    selectedProvider,
    sizeLabel,
    typeLabel,
    linkedOrderId,
    partner,
    loading,
    cancelLoading,
    showCancelModal,
    setShowCancelModal,
    loadDetail,
    handlePay,
    handleCancel,
    openTracking,
    openGuide,
    isPendingPayment,
    breadcrumbs,
  };
};
